import React, { useMemo, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { withRouter } from 'hooks/withRouter'
import { apiRequest } from 'store/Misc/apiRequest'
import CreateHeader from './CreateHeader'
import StepSidebar from './StepSidebar'
import CreateFooterNav from './CreateFooterNav'
import TargetingSummaryPanel from './TargetingSummaryPanel'
import BasicDetailsStep from './steps/BasicDetailsStep'
import TargetingStep from './steps/TargetingStep'
import BudgetingStep from './steps/BudgetingStep'
import CreativeUploadStep from './steps/CreativeUploadStep'
import ReviewStep from './steps/ReviewStep'

const STEPS = [
  { key: 'basic_details', labelKey: 'rewardAds.createFlow.steps.basic_details' },
  { key: 'targeting', labelKey: 'rewardAds.createFlow.steps.targeting' },
  { key: 'budgeting', labelKey: 'rewardAds.createFlow.steps.budgeting' },
  { key: 'creative_upload', labelKey: 'rewardAds.createFlow.steps.creative_upload' },
  { key: 'review', labelKey: 'rewardAds.createFlow.steps.review' }
]

const INITIAL_FORM = {
  title: '',
  callbackUrl: '',
  ctaMessage: '',
  description: '',
  interests: [],
  locations: [],
  ipRanges: [],
  dailyBudget: '',
  startDate: '',
  endDate: '',
  daysToDisplay: [],
  startTime: '',
  endTime: '',
  frequencyId: '',
  frequency: '',
  scheduleIds: [],
  maxViewsPerUserDay: ''
}

const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-./?%&=]*)?$/i

function extractErrorMessages(error) {
  try {
    const parsed = JSON.parse(error.message)
    if (Array.isArray(parsed.message) && parsed.message.length) return parsed.message
  } catch (_parseError) {
    // not a structured apiRequest error — fall through to the generic message below
  }
  return ['Unable to create reward ad campaign']
}

export default withTranslation()(withRouter(function CreateRewardAd({ t, router: { navigate } }) {
  const [step, setStep] = useState(0)
  const [furthestStep, setFurthestStep] = useState(0)
  const [form, setForm] = useState(INITIAL_FORM)
  const [file, setFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const canGoNext = useMemo(() => {
    if (step === 0) {
      return form.title.trim().length > 1 && URL_PATTERN.test(form.callbackUrl.trim()) &&
        form.ctaMessage.trim().length > 1 && form.description.trim().length > 1
    }
    if (step === 1) return true
    if (step === 2) {
      return Number(form.dailyBudget) > 0 && !!form.startDate && !!form.endDate &&
        form.daysToDisplay.length > 0 && !!form.frequencyId && form.scheduleIds.length > 0
    }
    if (step === 3) return !!file && file.progress >= 100
    return true
  }, [step, form, file])

  const isLastStep = step === STEPS.length - 1

  const goBack = () => setStep((prev) => Math.max(0, prev - 1))

  // promos/campaigns only accepts the fields built below (see v1's CreateCampaign.js
  // for reference) — interests/locations/IP-targeting/max-views collected in earlier
  // steps have no matching field on this endpoint yet, so they aren't sent.
  const submit = async () => {
    setSubmitting(true)

    try {
      const duration = Math.max(1, dayjs(form.endDate).diff(dayjs(form.startDate), 'day') + 1)
      const totalBudget = Math.round((Number(form.dailyBudget) || 0) * duration)

      const formData = new FormData()
      formData.append('title', form.title)
      formData.append('callback_url', form.callbackUrl)
      formData.append('message', form.ctaMessage)
      formData.append('description', form.description)
      formData.append('type', file.rawFile.type.startsWith('video') ? 'video' : 'flyer')
      formData.append('budget', totalBudget)
      formData.append('promo_frequency_id', form.frequencyId)
      formData.append('start', dayjs(form.startDate).format('YYYY-MM-DD'))
      formData.append('end', dayjs(form.endDate).format('YYYY-MM-DD'))
      formData.append('media', file.rawFile)
      formData.append('professions', JSON.stringify([]))
      formData.append('interests', JSON.stringify([]))
      formData.append('schedules', JSON.stringify(form.scheduleIds))

      await apiRequest({ method: 'post', url: 'promos/campaigns', payload: formData })

      toast.success(t('rewardAds.createFlow.created_toast'))
      navigate('/reward-ads')
    } catch (error) {
      extractErrorMessages(error).forEach((message) => toast.error(message))
    } finally {
      setSubmitting(false)
    }
  }

  const goNext = () => {
    if (!canGoNext) return

    if (isLastStep) {
      submit()
      return
    }

    setStep((prev) => {
      const next = Math.min(STEPS.length - 1, prev + 1)
      setFurthestStep((furthest) => Math.max(furthest, next))
      return next
    })
  }

  const goToStep = (index) => {
    if (index <= furthestStep) setStep(index)
  }

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      <CreateHeader onClose={() => navigate('/reward-ads')} />

      <div className="flex flex-1 min-h-0">
        <StepSidebar steps={STEPS} currentStep={step} furthestStep={furthestStep} onNavigate={goToStep} />

        <main className="flex-1 min-w-0 overflow-y-auto px-10 py-8">
          {step === 0 && <BasicDetailsStep form={form} updateField={updateField} />}
          {step === 1 && <TargetingStep form={form} updateField={updateField} />}
          {step === 2 && <BudgetingStep form={form} updateField={updateField} />}
          {step === 3 && <CreativeUploadStep file={file} setFile={setFile} />}
          {step === 4 && <ReviewStep form={form} file={file} onEditStep={goToStep} />}
        </main>

        <TargetingSummaryPanel form={form} />
      </div>

      <CreateFooterNav
        step={step}
        isLastStep={isLastStep}
        canGoNext={canGoNext}
        submitting={submitting}
        onBack={goBack}
        onNext={goNext}
      />
    </div>
  )
}))
